import { useState, useRef, useContext, useEffect } from "react";
import companyBasedRequest from "../../request/companyBasedRequest";
import { CombinedContext } from "../../context/combinedContext";
import { ownerFirst } from "../../utils/ownerFirst";
import generalRequest from "../../request/generalRequest";
import "./profileAdminCard.css";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import ShopInfoModal from "./shopInfoModal/shopInfoModal";
import ImageModal from "./imageModal/imageModal";
import ShopHoursModal from "./shopHoursModal/shopHoursModal";
import { Camera, ChevronDown, Edit, Flag, MapPin, Phone } from "lucide-react";
import ShopContactModal from "./shopContactModal/shopContactModal";
import AdminServicesModal from "./AdminServicesModal/AdminServicesModal";
import AttributesModal from "./attributesModal/attributesModal";
import ReportReviewModalAdmin from "../reportReviewModal/reportReviewModalAdmin";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ProfileAdminSkeleton from "./profileAdminSkeleton/profileAdminSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import ShopGallery from "../common/shopGallery/shopGallery";
import AdminServiceCategoriesModal from "./AdminServiceCategoriesModal/AdminServiceCategoriesModal";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

const ProfileAdminCard = () => {
	const { userData, language } = useContext(CombinedContext);
	const [shopData, setShopData] = useState(null);
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showAllHours, setShowAllHours] = useState(false);
	const [activeModal, setActiveModal] = useState(null);
	const [reportReviewId, setReportReviewId] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [banner, setBanner] = useState({ message: "", type: "error" });
	const [openCategories, setOpenCategories] = useState({});
	const [showFullWeek, setShowFullWeek] = useState(false);


	const fileInputRef = useRef(null);
	const staffListRef = useRef(null);
	const isDraggingRef = useRef(false);
	const startXRef = useRef(0);
	const scrollLeftRef = useRef(0);
	const velocityRef = useRef(0);
	const lastXRef = useRef(0);
	const categoryRefs = useRef({});
	let momentumID = null;

	const location = useLocation();

	const navigate = useNavigate();

	const showBanner = (message, type = "error") => {
		setBanner({ message, type });
		if (type === "success") {
			setTimeout(() => setBanner({ message: "", type: "error" }), 3000);
		}
	};

	useEffect(() => {
		if (!location.state?.openModal) return;

		setActiveModal(location.state.openModal);
	}, [location.state, navigate]);

	useEffect(() => {
		async function loadData() {
			try {
				if (!userData?.user?.shopId) return;
				const shopRes = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);
				setShopData(shopRes.shop);
				setImages(shopRes.shop.images || []);
				setLoading(false);
			} catch (err) {
				console.error("Error loading data:", err);
				setLoading(false);
				showBanner(language === "es" ? "No pudimos cargar la información de tu negocio. Inténtalo de nuevo." : "We couldn't load your business information. Please try again.");
			}
		}
		loadData();
	}, [userData]);

	useEffect(() => {
		if (shopData?.serviceCategories?.length && !activeCategory) {
			setActiveCategory(shopData.serviceCategories[0].categoryId);
		}
	}, [shopData, activeCategory]);

	const servicesByCategory = (shopData?.serviceCategories || []).map((cat) => ({
		category: cat,
		services: (shopData.services || []).filter((s) => s.categoryId === cat.categoryId),
	}));

	useEffect(() => {
		if (!servicesByCategory.length) return;
		setOpenCategories(
			Object.fromEntries(servicesByCategory.map(({ category }) => [category.categoryId, true]))
		);
	}, [shopData]);

	const toggleCategory = (id) =>
		setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));



	const openModal = (name) => setActiveModal(name);
	const closeModal = () => setActiveModal(null);

	if (loading || !shopData) return <ProfileAdminSkeleton />;

	const handleStaffMouseDown = (e) => {
		const slider = staffListRef.current;
		if (!slider) return;

		isDraggingRef.current = true;
		slider.classList.add("is-dragging");
		startXRef.current = e.pageX - slider.offsetLeft;
		scrollLeftRef.current = slider.scrollLeft;
	};

	const handleStaffMouseLeave = () => {
		const slider = staffListRef.current;
		if (!slider) return;

		isDraggingRef.current = false;
		slider.classList.remove("is-dragging");
	};

	const handleStaffMouseUp = () => {
		const slider = staffListRef.current;
		if (!slider) return;

		isDraggingRef.current = false;
		slider.classList.remove("is-dragging");
	};

	const handleStaffMouseMove = (e) => {
		const slider = staffListRef.current;
		if (!slider || !isDraggingRef.current) return;

		e.preventDefault();
		const x = e.pageX - slider.offsetLeft;
		const walk = (x - startXRef.current) * 1; // drag speed
		slider.scrollLeft = scrollLeftRef.current - walk;
	};


	function formatPhone(number) {
		if (!number) return "";
		// remove all non-digits
		const digits = number.replace(/\D/g, "");

		// US/Canada format (E.164: +1xxxxxxxxxx)
		if (digits.length === 11 && digits.startsWith("1")) {
			return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
		}

		// fallback (just add + if missing)
		return number.startsWith("+") ? number : "+" + number;
	}

	function handleReportReview(reviewId) {
		setReportReviewId(reviewId);
		setActiveModal("reportReview");
	}

	const reviewCount = shopData.reviews?.length || 0;
	const rating = (
		shopData.reviewSummary?.averageRating || (reviewCount > 0 ? shopData.reviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount : 0)
	).toFixed(1);
	const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);

	const renderStars = (rating) => {
		const stars = [];

		for (let i = 1; i <= 5; i++) {
			stars.push(
				<span key={i} className={i <= rating ? "sf-star sf-star-filled" : "sf-star sf-star-empty"}>
					★
				</span>,
			);
		}

		return stars;
	};

	function formatTime24To12(time24, language = "en") {
		if (!time24) return "";

		const [h, m] = time24.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = m.toString().padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		// If you ever want ES = 24h, you can branch here
		return `${hour12}:${minutes} ${period}`;
	}

	const formatUrl = (url) => {
		if (!url) return "";
		if (url.startsWith("http://") || url.startsWith("https://")) {
			return url;
		}
		return `https://${url}`;
	};

	return (
		<div className="sf-adm-page">
			<div className="sf-adm-content">
				<div className="sf-adm-main">
					{/* ── GALLERY ── */}
					<div className="sf-adm-gallery">
						<ShopGallery
							images={shopData.images || []}
							shareUrl={`https://reservasinfila.com/shops/${shopData.slug || shopData.shopId}`}
						/>
						<button type="button" className="sf-adm-photo-btn" onClick={() => openModal("images")}>
							<Camera size={15} strokeWidth={1.9} />
							{translations[language].profile.images.title}
						</button>
					</div>

					{/* ── IDENTITY ── */}
					<header className="sf-adm-head">
						<div className="sf-adm-title-row">
							<h1 className="sf-shop-name">
								{shopData.name || <span className="sf-adm-placeholder">{translations[language].profile.addShopName}</span>}
							</h1>
							<button type="button" className="sf-adm-edit" onClick={() => openModal("info")} aria-label={translations[language].profile.modal.shopInfo.title}>
								<Edit size={14} />
							</button>
						</div>

						<div className="sf-shop-meta">
							{reviewCount > 0 ? (
								<span className="sf-shop-rating-pill">
									<span className="sf-adm-star">★</span>
									<strong>{rating}</strong>
									<span>({reviewCount} {translations[language].shopPage.main.reviews})</span>
								</span>
							) : (
								<span className="sf-shop-new-pill">{translations[language].profile.noReviewsyet}</span>
							)}
						</div>

						<p className="sf-shop-address">
							<MapPin size={15} strokeWidth={1.7} />
							<span>
								{shopData.address && shopData.city && shopData.country ? (
									shopData.address
								) : (
									<span className="sf-adm-placeholder">{translations[language].profile.addAddress}</span>
								)}
								{shopData.addressDescription && <em>{shopData.addressDescription}</em>}
							</span>
						</p>
					</header>

					{/* ── SERVICES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{translations[language].profile.services.title}</h2>
							{shopData.services?.length > 0 && <span className="sf-shop-count">{shopData.services.length}</span>}
							<button type="button" className="sf-adm-edit sf-adm-edit--end" onClick={() => openModal("services")}>
								<Edit size={14} />
								{translations[language].profile.addServices}
							</button>
						</div>

						{shopData.services?.length > 0 ? (
							<div className="sf-shop-services">
								<div className="sf-shop-cat-tabs">
									{shopData.serviceCategories.map((cat) => (
										<button
											key={cat.categoryId}
											type="button"
											className={`sf-shop-cat-tab ${activeCategory === cat.categoryId ? "is-active" : ""}`}
											onClick={() => {
												setActiveCategory(cat.categoryId);
												categoryRefs.current[cat.categoryId]?.scrollIntoView({ behavior: "smooth", block: "start" });
											}}
										>
											{cat.name?.[language]}
										</button>
									))}
									<button type="button" className="sf-adm-edit sf-adm-edit--chip" onClick={() => openModal("category")} aria-label={translations[language].profile.categories.categories}>
										<Edit size={12} />
									</button>
								</div>

								{servicesByCategory.map(({ category, services }) => {
									const isOpen = openCategories[category.categoryId] !== false;
									return (
										<div key={category.categoryId} ref={(el) => (categoryRefs.current[category.categoryId] = el)} className="sf-shop-cat-card">
											<div className="sf-adm-cat-header">
												<button type="button" className="sf-adm-cat-toggle" onClick={() => toggleCategory(category.categoryId)} aria-expanded={isOpen}>
													<h3>{category.name?.[language]}</h3>
													<span className="sf-shop-cat-meta">
														{services.length}
														<ChevronDown size={16} className={`sf-adm-chevron ${isOpen ? "is-open" : ""}`} />
													</span>
												</button>
												<button
													type="button"
													className="sf-adm-cat-add"
													onClick={(e) => {
														e.stopPropagation();
														openModal("services");
													}}
												>
													{services.length > 0 ? translations[language].profile.addServices : translations[language].profile.addFirstServices}
												</button>
											</div>

											<div className={`sf-shop-cat-body ${isOpen ? "is-open" : ""}`}>
												<div>
													{services.length > 0 ? (
														services.map((s) => (
															<div key={s.serviceId} className="sf-shop-service">
																<div className="sf-shop-service-copy">
																	<h4 className="sf-shop-service-name">
																		{s.name}
																		{s.isHomeService && (
																			<span className="sf-shop-service-tag is-home">
																				{language === "es" ? "A domicilio" : "Home service"}
																			</span>
																		)}
																	</h4>
																	{s.description && <p className="sf-shop-service-desc">{s.description}</p>}
																	<span className="sf-shop-service-time">{s.duration} min</span>
																</div>
																<div className="sf-shop-service-action">
																	<span className="sf-shop-service-price">
																		{s.currency || "USD"} {s.price ?? 0}
																	</span>
																</div>
															</div>
														))
													) : (
														<button type="button" className="sf-adm-empty-row" onClick={() => openModal("services")}>
															{translations[language].profile.footerEmpty}
														</button>
													)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<button type="button" className="sf-adm-empty" onClick={() => openModal("services")}>
								{translations[language].profile.addFirstServices}
							</button>
						)}
					</section>

					{/* ── ATTRIBUTES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{translations[language].shopPage.main.attributes}</h2>
							<button type="button" className="sf-adm-edit sf-adm-edit--end" onClick={() => openModal("attributes")}>
								<Edit size={14} />
							</button>
						</div>

						{shopData.attributes?.length > 0 ? (
							<div className="sf-shop-attrs">
								{shopData.attributes.map((attr) => (
									<span className="sf-shop-attr" key={attr.attributeId}>
										<span className="sf-shop-attr-icon">
											{attr.icon ? <img src={attr.icon} alt="" /> : <span className="sf-adm-dot" />}
										</span>
										{language === "es" ? attr.nameES : attr.nameEN}
									</span>
								))}
							</div>
						) : (
							<button type="button" className="sf-adm-empty" onClick={() => openModal("attributes")}>
								{translations[language].profile.addAttributes}
							</button>
						)}
					</section>

					{/* ── REVIEWS ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{translations[language].profile.reviews}</h2>
							{reviewCount > 0 && <span className="sf-shop-count">{reviewCount}</span>}
						</div>

						{reviewCount > 0 ? (
							<>
								<div className="sf-shop-rev-summary">
									<div className="sf-shop-rev-score">
										<span className="sf-shop-rev-score-num">{rating}</span>
										<span className="sf-adm-stars">{renderStars(Math.round(rating))}</span>
										<span className="sf-shop-rev-score-count">
											{reviewCount} {translations[language].shopPage.main.reviews}
										</span>
									</div>

									<div className="sf-shop-rev-bars">
										{[5, 4, 3, 2, 1].map((star) => {
											const count = shopData.reviews.filter((r) => r.rating === star).length;
											const percent = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
											return (
												<div key={star} className="sf-shop-rev-bar-row">
													<span className="sf-shop-rev-bar-label">{star} ★</span>
													<span className="sf-shop-rev-bar">
														<span className="sf-shop-rev-bar-fill" style={{ width: `${percent}%` }} />
													</span>
													<span className="sf-shop-rev-bar-count">{count}</span>
												</div>
											);
										})}
									</div>
								</div>

								<div className="sf-shop-rev-list">
									{shopData.reviews.map((rev) => (
										<article key={rev.reviewId} className="sf-shop-rev-card">
											<div className="sf-shop-rev-head">
												<div className="sf-shop-rev-who">
													<span className="sf-shop-rev-avatar">{(rev.customerName || "?").charAt(0).toUpperCase()}</span>
													<span>
														<span className="sf-shop-rev-name">{rev.customerName || "Anonymous"}</span>
														<span className="sf-shop-rev-date">{new Date(rev.createdAt).toLocaleDateString()}</span>
													</span>
												</div>
												<div className="sf-shop-rev-actions">
													<span className="sf-adm-stars">{renderStars(rev.rating)}</span>
													<button type="button" className="sf-shop-rev-flag" onClick={() => handleReportReview(rev.reviewId)} title="Report review">
														<Flag size={13} />
													</button>
												</div>
											</div>

											{rev.services?.length > 0 && (
												<div className="sf-shop-rev-chips">
													{rev.services.map((srv, i) => (
														<span key={i} className="sf-shop-rev-chip">
															{language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
															<em>{srv.duration} min</em>
														</span>
													))}
												</div>
											)}

											{rev.comment && <p className="sf-shop-rev-text">{rev.comment}</p>}
										</article>
									))}
								</div>
							</>
						) : (
							<div className="sf-adm-empty is-static">{translations[language].profile.noReviewsyet}</div>
						)}
					</section>
				</div>

				{/* ── SIDEBAR ── */}
				<aside className="sf-adm-sidebar">
					<div className="sf-shop-card">
						{shopData.lat && shopData.lng && (
							<div className="sf-shop-map">
								<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
							</div>
						)}

						<div className="sf-shop-card-body">
							<div className="sf-adm-block-head">
								<p className="sf-shop-label">{translations[language].shopPage.sidebar.description}</p>
								<button type="button" className="sf-adm-edit" onClick={() => openModal("info")}>
									<Edit size={14} />
								</button>
							</div>
							{shopData.description ? (
								<p className="sf-adm-desc">{shopData.description}</p>
							) : (
								<button type="button" className="sf-adm-placeholder is-action" onClick={() => openModal("info")}>
									{translations[language].profile.modal.shopInfo.description}
								</button>
							)}

							<div className="sf-shop-rule" />

							<div className="sf-adm-block-head">
								<p className="sf-shop-label">{translations[language].profile.sidebar.contact.title}</p>
								<button type="button" className="sf-adm-edit" onClick={() => openModal("contact")}>
									<Edit size={14} />
								</button>
							</div>
							<a href={shopData.phone ? `tel:${shopData.phone}` : undefined} className="sf-shop-phone">
								<Phone size={15} strokeWidth={1.7} />
								{shopData.phone ? formatPhone(shopData.phone) : translations[language].profile.sidebar.contact.noPhone}
							</a>

							<div className="sf-shop-rule" />

							<div className="sf-adm-block-head">
								<p className="sf-shop-label">{translations[language].profile.sidebar.hours.title}</p>
								<button type="button" className="sf-adm-edit" onClick={() => openModal("hours")}>
									<Edit size={14} />
								</button>
							</div>

							{shopData.openingHours && shopData.openingHours.length > 0 ? (
								<div className="sf-shop-hours">
									<div className="sf-shop-hours-today">
										<span className="sf-shop-hours-day">{translations[language].shopPage.sidebar.hours?.today || "Today"}</span>
										<span className="sf-shop-hours-time">
											{(() => {
												const todayIndex = (new Date().getDay() + 6) % 7;
												const todayObj = shopData.openingHours[todayIndex];
												return todayObj?.closed
													? translations[language].profile.sidebar.hours.closed
													: `${formatTime24To12(todayObj?.open, language)} – ${formatTime24To12(todayObj?.close, language)}`;
											})()}
										</span>
									</div>

									<button type="button" className="sf-shop-hours-toggle" onClick={() => setShowFullWeek((v) => !v)}>
										{showFullWeek
											? language === "es"
												? "Ocultar semana"
												: "Hide full week"
											: language === "es"
												? "Ver semana completa"
												: "Show full week"}
										<ChevronDown size={14} className={`sf-adm-chevron ${showFullWeek ? "is-open" : ""}`} />
									</button>

									<div className={`sf-shop-hours-week ${showFullWeek ? "is-open" : ""}`}>
										<div>
											{shopData.openingHours.map((day, index) => {
												const todayIndex = (new Date().getDay() + 6) % 7;
												return (
													<div key={index} className={`sf-shop-hours-row ${todayIndex === index ? "is-today" : ""}`}>
														<span>{language === "es" ? day.dayES : day.dayEN}</span>
														<span>
															{day.closed
																? translations[language].profile.sidebar.hours.closed
																: `${formatTime24To12(day.open, language)} – ${formatTime24To12(day.close, language)}`}
														</span>
													</div>
												);
											})}
										</div>
									</div>
								</div>
							) : (
								<button type="button" className="sf-adm-placeholder is-action" onClick={() => openModal("hours")}>
									{language === "es" ? "Añadir horario de apertura" : "Add opening hours"}
								</button>
							)}

							{shopData.socialLinks && Object.values(shopData.socialLinks).some((link) => link) && (
								<>
									<div className="sf-shop-rule" />
									<div className="sf-adm-block-head">
										<p className="sf-shop-label">{translations[language].shopPage.sidebar.socialTitle}</p>
										<button type="button" className="sf-adm-edit" onClick={() => openModal("contact")}>
											<Edit size={14} />
										</button>
									</div>
									<div className="sf-adm-socials">
										{shopData.socialLinks.instagram && (
											<a href={formatUrl(shopData.socialLinks.instagram)} target="_blank" rel="noopener noreferrer" className="sf-adm-social">
												<img src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png" alt="" />
												<span>Instagram</span>
											</a>
										)}
										{shopData.socialLinks.facebook && (
											<a href={formatUrl(shopData.socialLinks.facebook)} target="_blank" rel="noopener noreferrer" className="sf-adm-social">
												<img src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png" alt="" />
												<span>Facebook</span>
											</a>
										)}
										{shopData.socialLinks.tiktok && (
											<a href={formatUrl(shopData.socialLinks.tiktok)} target="_blank" rel="noopener noreferrer" className="sf-adm-social">
												<img src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png" alt="" />
												<span>TikTok</span>
											</a>
										)}
										{shopData.socialLinks.website && (
											<a href={formatUrl(shopData.socialLinks.website)} target="_blank" rel="noopener noreferrer" className="sf-adm-social">
												<img src="https://cdn-icons-png.flaticon.com/512/545/545674.png" alt="" />
												<span>{translations[language].shopPage.sidebar.websiteTitle}</span>
											</a>
										)}
									</div>
								</>
							)}
						</div>
					</div>

					{/* ── TEAM ── */}
					<div className="sf-adm-card">
						<div className="sf-adm-block-head">
							<p className="sf-shop-label">{translations[language].shopPage.sidebar.staff}</p>
							<button type="button" className="sf-adm-edit" onClick={() => navigate("/team")}>
								<Edit size={14} />
							</button>
						</div>

						<div
							className="sf-adm-team"
							ref={staffListRef}
							onMouseDown={handleStaffMouseDown}
							onMouseLeave={handleStaffMouseLeave}
							onMouseUp={handleStaffMouseUp}
							onMouseMove={handleStaffMouseMove}
						>
							{ownerFirst(shopData, shopData.staffs).map((b) => (
								<div className="sf-adm-team-card" key={b.staffId || b.email}>
									{b.photo ? (
										<img className="sf-adm-team-avatar" src={b.photo} alt="" />
									) : (
										<span className="sf-adm-team-avatar is-fallback">
											{(b.firstName || b.email || "?").charAt(0).toUpperCase()}
										</span>
									)}
									<span className="sf-adm-team-name">{b.firstName || b.email?.split("@")[0] || "Invited"}</span>
								</div>
							))}
						</div>
					</div>
				</aside>
			</div>

			{activeModal === "info" && <ShopInfoModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />}
			{activeModal === "services" && (
				<AdminServicesModal
					isOpen={true}
					onClose={closeModal}
					shopId={shopData.shopId}
					categories={shopData.serviceCategories}
					initialServices={shopData.services || []}
					servicesToAdd={[]}
					onSave={(updated) => {
						setShopData((prev) => ({ ...prev, services: updated }));
					}}
					onOpenCategories={() => {
						setActiveModal("category");
					}}
					onSuccess={(msg) => showBanner(msg, "success")}
				/>
			)}
			{activeModal === "images" && (
				<ImageModal
					isOpen={true}
					shopId={shopData.shopId}
					initialImages={shopData.images || []}
					onClose={closeModal}
					onSave={(updatedImages) => {
						setShopData((prev) => ({
							...prev,
							images: updatedImages,
						}));
					}}
				/>
			)}
			{activeModal === "hours" && <ShopHoursModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />}
			{activeModal === "contact" && <ShopContactModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />}
			{activeModal === "attributes" && (
				<AttributesModal open={true} onClose={closeModal} shopId={shopData.shopId} shopData={shopData} setShopData={setShopData} />
			)}
			{activeModal === "reportReview" && (
				<ReportReviewModalAdmin
					open={true}
					onClose={() => {
						setReportReviewId(null);
						closeModal();
					}}
					reviewId={reportReviewId}
					shopId={shopData.shopId}
				/>
			)}
			{activeModal === "category" && (
				<AdminServiceCategoriesModal
					isOpen
					language={language}
					shopId={shopData.shopId}
					initialCategories={shopData.serviceCategories}
					services={shopData.services}
					onClose={() => {
						closeModal();
					}}
					onSave={(updated) => {
						// setDraftCategories(updated);
						// setIsDirty(true);
					}}
				/>
			)}

			{banner.message && <BannerMessageFixed type={banner.type} text={banner.message} />}
		</div>
	);
};

export default ProfileAdminCard;
